<template>
  <main class='Home' ref='mainEl' :style="{ '--header-ink': headerInk }">
    <MainHeader :class='{ "header-visible": reasonsToShow || scrolledPast || search.active || searchRecentlyActive }' :from='back' :pageToGoBackTo='pageToGoBackTo' />
    <SearchOverlay />
    <veil :showveil='reasonsToShow || search.active' :startcolor='startcolor' :endcolor='endcolor'/>
    <RouterViewWithTransitions id='Overlay'
                               name='overlay'
                               :key='to'
                               :origin='to'
                               @reasonsToShowTheVeil="changeToShowTheVeil"
                               @pageToGoBackTo="getPageToGoBackTo"
                               @changeColors="changeColors"/>
    <aside><Navigation /></aside>
    <Layout class='content' :content='kql.site.layout' :page='kql.site' fill='white'/>
    <router-view />
    <footer>
      <FooterLayout :content='kql.site.footer' :page='kql.site' fill='#141414'/>
      <SubscribePopup :source='kql.site' />
      <Cart />
    </footer>
  </main>
</template>

<script setup>
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { useStore } from '../KQL.js'
  import { useRouter } from 'vue-router'
  const kql = useStore();
  import RouterViewWithTransitions from '../components/ViewTransitions.vue'
  import Signup from '../components/Signup.vue'

  import Layout from '../components/Layout.vue'
  import Veil from '../components/Veil.vue'
  import FooterLayout from '../components/Footer.vue'
  import MainHeader from '../components/MainHeader.vue'
  import Navigation from '../components/Navigation.vue'
  import SubscribePopup from '../components/SubscribePopup.vue'
  import Cart from '../components/Cart.vue'
  import SearchOverlay from '../components/SearchOverlay.vue'
  import { useSearchStore } from '../components/SearchStore.js'
  const search = useSearchStore();
  const GRADIENT_STOPS = [
    [0,   [48,  25,  30]],
    [5,   [48,  25,  30]],
    [18,  [83,  71,  83]],
    [28,  [139, 118,  99]],
    [40,  [255, 176,   5]],
    [100, [255, 176,   5]],
  ];

  function sampleGradient(pct) {
    for (let i = 1; i < GRADIENT_STOPS.length; i++) {
      const [p0, c0] = GRADIENT_STOPS[i - 1];
      const [p1, c1] = GRADIENT_STOPS[i];
      if (pct <= p1) {
        const t = (pct - p0) / (p1 - p0);
        return c0.map((v, j) => Math.round(v + t * (c1[j] - v)));
      }
    }
    return GRADIENT_STOPS[GRADIENT_STOPS.length - 1][1];
  }

  function inkFromBackground(rgb) {
    const lum = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
    const t = Math.max(0, Math.min(1, (lum - 0.20) / 0.40));
    const light = [224, 224, 224], dark = [20, 20, 20];
    return `rgb(${light.map((v, i) => Math.round(v + t * (dark[i] - v))).join(',')})`;
  }

  const mainEl = ref(null);
  const scrollInk = ref('rgb(224,224,224)');
  const overlayActive = computed(() => reasonsToShow.value > 0 || search.active);
  const headerInk = computed(() => overlayActive.value ? 'rgb(224,224,224)' : scrollInk.value);

  const reasonsToShow = ref(0);
  const scrolledPast = ref(false);
  const SCROLL_THRESHOLD = 250;

  function onScroll() {
    scrolledPast.value = window.scrollY > SCROLL_THRESHOLD;
    if (mainEl.value) {
      const headerMidY = window.scrollY + 87;
      const pct = Math.max(0, Math.min(100, (headerMidY / mainEl.value.offsetHeight) * 100));
      scrollInk.value = inkFromBackground(sampleGradient(pct));
    }
  }
  onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }));
  onUnmounted(() => window.removeEventListener('scroll', onScroll));
  const startcolor = ref(0);
  const endcolor = ref(0);
  const pageToGoBackTo = ref(false);
  const back = ref();
  const to = ref();
  // Keep header visible briefly after search closes so the nav doesn't
  // instantly vanish if the user scrolled back to the top while searching.
  const searchRecentlyActive = ref(false);
  let searchHideTimer = null;
  watch(() => search.active, (active) => {
    if (active) {
      searchRecentlyActive.value = true;
      clearTimeout(searchHideTimer);
    } else {
      searchHideTimer = setTimeout(() => { searchRecentlyActive.value = false; }, 500);
    }
  });

  const router = useRouter();
  router.beforeEach(async (toRoute, from) => {
    search.close();
    back.value = from;
    to.value = toRoute;
  })
  const getPageToGoBackTo = function(path){
    if(!pageToGoBackTo.value) pageToGoBackTo.value = path;
  }
  const changeColors = function(newstartcolor, newendcolor){
    startcolor.value = newstartcolor;
    endcolor.value = newendcolor;
  }
  const changeToShowTheVeil = function(reasons){
    reasonsToShow.value = reasonsToShow.value + reasons;
  }
</script>

<style scoped>
@import '../css/breakpoints.css';

  aside {
    position: fixed;
    right: 9rem;
    bottom: 0;
    top: 0;
  }

  footer {
    margin-top: 10rem;
  }

  main {
    --border: var(--second);
    color: rgb(100,70,70);
    min-height: 100vh;
    background: linear-gradient(180deg, rgba(48,25,30,1) 0%, rgba(48,25,30,1) 5%, rgba(83,71,83,1) 18%, rgba(139,118,99,1) 28%, rgba(255,176,5,1) 40%, rgba(255,176,5,1) 100%);
  }

  @media (--desktop) {
    main {
      padding: 6rem 12rem;
    }
  }

  @media (--phone) {
    main {
      padding: 3rem 4rem;
      padding-right: 9rem;
    }
  }

</style>

<style>
  @media print {
    main.Home > article.content{
      display: v-bind( reasonsToShow ? 'none' : 'block');
    }
  }

  main.Home article.content > section:nth-child(even).text {
    margin-left: 20%;
  }

  main.Home article.content > section.heading + section.text  {
    margin-left: 0%;
  }

  main.Home article.content > section:nth-child(even).logos {
    margin-left: 20%;
  }

  main.Home article.content > section:nth-child(even).intro {
    margin-left: 20%;
  }

  @media (--phone) {
    main.Home article.content > section:nth-child(even).text {
      margin-left: 0;
    }
    main.Home article.content >  section:nth-child(even).logos {
      margin-left: 0%;
    }
    main.Home article.content > section:nth-child(even).intro {
      margin-left: 0%;
    }
  }
</style>
